import { debugLog } from './debug';
import { DEFAULT_VIEW_SETTINGS } from '../../utils/campaignViews/index';

/**
 * Restores settings from a settings object
 * 
 * When a setting is not present in the provided settings object, we restore
 * to the default value. This ensures that switching between custom views
 * properly resets settings that one view has but another doesn't.
 *
 * Show/hide field choices are the exception: they are applied only when the
 * view saved them. A missing key must not write the built-in defaults back
 * onto the campaign.
 * 
 * @param {Object} settings - Settings to restore
 * @param {string} type - View type (flow, kanban, table, gantt, todolist)
 * @param {Object} setters - Object containing all setter functions
 * @param {Object} fokusHooks - Object containing fokus hooks for each view type
 * @param {React.MutableRefObject} isRestoringRef - Ref to track restoring state
 */
export function restoreSettingsForType(settings, type, setters, fokusHooks, isRestoringRef) {
  debugLog('restoreSettings', type, settings);
  if (!settings) return;

  isRestoringRef.current = true;
  
  // Get defaults for this view type
  const defaults = DEFAULT_VIEW_SETTINGS[type] || {};

  const {
    setFiltersPerView,
    setLocalSprint,
    setTableColumnVisibility,
    setTodoElementVisibility,
    onFlowViewVisibilityChange,
    onFlowViewportChange,
    setGanttSwimlane,
    onGanttViewportChange,
    setShowArchived,
    setShowActionableOnly,
    setShowNestedCampaigns,
    setTodoViewMode,
    setTodoSwimlaneGroupBy,
    setTodoSwimlane,
    setTodoShowNestedItems,
  } = setters;

  const { flowFokus, kanbanFokus, tableFokus, ganttFokus, todoFokus, neuralFokus } = fokusHooks;

  // Restore filters for this view type (use empty object as default)
  if (setFiltersPerView) {
    const filtersToRestore = settings.filters !== undefined ? settings.filters : (defaults.filters || {});
    setFiltersPerView(prev => ({
      ...prev,
      [type]: filtersToRestore,
    }));
  }

  // Restore sort setting for this view type
  if (setLocalSprint) {
    const sortToRestore = settings.sortSetting !== undefined ? settings.sortSetting : (defaults.sortSetting || 'manual');
    setLocalSprint(prev => {
      if (!prev) return prev;
      const currentPerView = prev.sortSettingsPerView || {};
      return {
        ...prev,
        sortSettingsPerView: { ...currentPerView, [type]: sortToRestore },
      };
    });
  }

  // Restore card display settings for this view type
  if (settings.cardDisplaySettings !== undefined && setLocalSprint) {
    setLocalSprint(prev => {
      if (!prev) return prev;
      const currentPerView = prev.cardDisplaySettings || {};
      return {
        ...prev,
        cardDisplaySettings: { ...currentPerView, [type]: settings.cardDisplaySettings },
      };
    });
  }

  // Restore swimlane setting (Kanban only)
  if (type === 'kanban' && setLocalSprint) {
    const swimlaneToRestore = settings.swimlaneSetting !== undefined ? settings.swimlaneSetting : (defaults.swimlaneSetting || 'none');
    setLocalSprint(prev => prev ? { ...prev, swimlaneSetting: swimlaneToRestore } : prev);
  }

  // Restore show/hide choices only when this view actually saved them.
  // A missing key keeps the campaign's current choices (or the field defaults
  // at read time) instead of writing the built-in stub back onto the campaign.
  if (type === 'table' && settings.columnVisibility !== undefined && setTableColumnVisibility) {
    setTableColumnVisibility(settings.columnVisibility);
  }

  if (type === 'todolist' && settings.elementVisibility !== undefined && setTodoElementVisibility) {
    setTodoElementVisibility(settings.elementVisibility);
  }

  // Restore flow view visibility (Flow only)
  if (type === 'flow' && onFlowViewVisibilityChange) {
    const flowVisToRestore = settings.flowViewVisibility !== undefined ? settings.flowViewVisibility : defaults.flowViewVisibility;
    if (flowVisToRestore) {
      onFlowViewVisibilityChange(flowVisToRestore);
    }
  }

  // Restore flow viewport (Flow only)
  if (type === 'flow' && settings.viewport !== undefined && onFlowViewportChange) {
    onFlowViewportChange(settings.viewport);
  }

  // Restore gantt swimlane setting (Gantt only)
  if (type === 'gantt' && setGanttSwimlane) {
    const swimlaneToRestore = settings.ganttSwimlane !== undefined ? settings.ganttSwimlane : (defaults.ganttSwimlane || 'none');
    setGanttSwimlane(swimlaneToRestore);
  }

  // Restore gantt viewport (Gantt only)
  if (type === 'gantt' && settings.viewport !== undefined && onGanttViewportChange) {
    onGanttViewportChange(settings.viewport);
  }

  // Restore archive/actionable settings (use false as default)
  if (setShowArchived) {
    setShowArchived(settings.showArchived !== undefined ? settings.showArchived : false);
  }
  if (setShowActionableOnly) {
    setShowActionableOnly(settings.showActionableOnly !== undefined ? settings.showActionableOnly : false);
  }
  if (setShowNestedCampaigns) {
    setShowNestedCampaigns(settings.showNestedCampaigns !== undefined ? settings.showNestedCampaigns : true);
  }

  // Restore Todo view mode and swimlane groupBy (Todo only)
  if (type === 'todolist') {
    if (setTodoViewMode) {
      const viewModeToRestore = settings.viewMode !== undefined ? settings.viewMode : (defaults.viewMode || 'master');
      setTodoViewMode(viewModeToRestore);
    }
    if (setTodoSwimlaneGroupBy) {
      const groupByToRestore = settings.swimlaneGroupBy !== undefined ? settings.swimlaneGroupBy : (defaults.swimlaneGroupBy || 'status');
      setTodoSwimlaneGroupBy(groupByToRestore);
    }
    // New: Restore swimlane setting (same as Kanban)
    if (setTodoSwimlane) {
      const swimlaneToRestore = settings.todoSwimlane !== undefined ? settings.todoSwimlane : (defaults.todoSwimlane || 'none');
      setTodoSwimlane(swimlaneToRestore);
    }
    // New: Restore show nested items toggle
    if (setTodoShowNestedItems) {
      const nestedToRestore = settings.todoShowNestedItems !== undefined ? settings.todoShowNestedItems : (defaults.todoShowNestedItems !== false);
      setTodoShowNestedItems(nestedToRestore);
    }
  }

  // Restore FOKUS mode for all views (use INSTANT methods to skip animations during view switching)
  // For Flow view, we update toolbar state SYNCHRONOUSLY so initialFocusedTaskIds is correct
  // on the first render. Then we call the instant method after a delay to update the flowchart ref.
  
  // SYNCHRONOUS: Update toolbar state for Flow view so initialFocusedTaskIds is correct
  if (type === 'flow') {
    if (settings.fokusMode && settings.fokusMode.isActive) {
      const fokusTaskIds = settings.fokusMode.focusedTaskIds || [];
      if (fokusTaskIds.length > 0 && flowFokus) {
        // Update toolbar state synchronously
        if (flowFokus.setIsFocusMode) {
          flowFokus.setIsFocusMode(true);
        }
        if (flowFokus.setFocusedTaskIds) {
          flowFokus.setFocusedTaskIds(new Set(fokusTaskIds));
        }
      }
    } else {
      // Clear FOKUS state synchronously
      if (flowFokus?.isFokusMode) {
        if (flowFokus.setIsFocusMode) {
          flowFokus.setIsFocusMode(false);
        }
        if (flowFokus.setFocusedTaskIds) {
          flowFokus.setFocusedTaskIds(null);
        }
      }
    }
  }
  
  // DELAYED: Call instant methods to update flowchart refs after component mounts
  const restoreFokus = () => {
    if (settings.fokusMode && settings.fokusMode.isActive) {
      const fokusTaskIds = settings.fokusMode.focusedTaskIds || [];
      debugLog('Restoring FOKUS mode', { type, fokusTaskIds, hasFlowFokus: !!flowFokus });
      if (fokusTaskIds.length > 0) {
        switch (type) {
          case 'flow':
            // Flow FOKUS uses instant method to skip animation
            debugLog('Restoring Flow FOKUS', { 
              hasEnterFokusModeInstant: !!flowFokus?.enterFokusModeInstant,
              hasEnterFokusMode: !!flowFokus?.enterFokusMode,
              fokusTaskIds 
            });
            if (flowFokus?.enterFokusModeInstant) {
              flowFokus.enterFokusModeInstant(fokusTaskIds);
            } else {
              flowFokus?.enterFokusMode?.(fokusTaskIds);
            }
            break;
          case 'kanban':
            kanbanFokus?.enterFokusMode?.(fokusTaskIds);
            break;
          case 'table':
            tableFokus?.enterFokusMode?.(fokusTaskIds);
            break;
          case 'gantt':
            ganttFokus?.enterFokusMode?.(fokusTaskIds);
            break;
          case 'todolist':
            todoFokus?.enterFokusMode?.(fokusTaskIds);
            break;
          case 'neural':
            neuralFokus?.enterFokusMode?.(fokusTaskIds);
            break;
          default:
            break;
        }
      }
    } else {
      // Exit FOKUS mode if it was active but settings say it shouldn't be (use INSTANT methods)
      switch (type) {
        case 'flow':
          if (flowFokus?.isFokusMode) {
            if (flowFokus?.exitFokusModeInstant) {
              flowFokus.exitFokusModeInstant();
            } else {
              flowFokus?.exitFokusMode?.();
            }
          }
          break;
        case 'kanban':
          if (kanbanFokus?.isFokusMode) kanbanFokus?.exitFokusMode?.();
          break;
        case 'table':
          if (tableFokus?.isFokusMode) tableFokus?.exitFokusMode?.();
          break;
        case 'gantt':
          if (ganttFokus?.isFokusMode) ganttFokus?.exitFokusMode?.();
          break;
        case 'todolist':
          if (todoFokus?.isFokusMode) todoFokus?.exitFokusMode?.();
          break;
        case 'neural':
          if (neuralFokus?.isFokusMode) neuralFokus?.exitFokusMode?.();
          break;
        default:
          break;
      }
    }
  };
  
  // Delay FOKUS restoration slightly to ensure view components are mounted
  // This is especially important for Flow view which uses refs
  setTimeout(restoreFokus, 50);

  // Allow saves again after a short delay
  setTimeout(() => {
    isRestoringRef.current = false;
  }, 100);
}
